import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  ChevronLeft,
  User,
  Building2,
  Briefcase,
  Brain,
  Loader2,
  CheckCircle,
  Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const industries = [
  "Technology",
  "Healthcare",
  "Finance & Banking",
  "Retail & E-commerce",
  "Manufacturing",
  "Professional Services",
  "Education",
  "Real Estate",
  "Media & Entertainment",
  "Transportation & Logistics",
  "Energy & Utilities",
  "Government",
  "Nonprofit",
  "Other"
];

const roles = [
  "CEO / Founder",
  "CTO / CIO",
  "CFO",
  "COO",
  "VP / Director of Operations",
  "VP / Director of Technology",
  "VP / Director of Strategy",
  "Product Manager",
  "Department Head",
  "Senior Manager",
  "Consultant",
  "Other Executive"
];

const companySizes = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1000 employees",
  "1000+ employees"
];

const aiExperiences = [
  { value: "none", label: "No AI experience - Complete beginner" },
  { value: "beginner", label: "Beginner - Used AI tools like ChatGPT" },
  { value: "intermediate", label: "Intermediate - Implemented AI in projects" },
  { value: "advanced", label: "Advanced - Led AI initiatives" }
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    industry: "",
    role: "",
    company_size: "",
    ai_experience: "beginner"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      if (response.data.profile) {
        setProfile(response.data.profile);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-6">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="bg-[#0a0a0a] border-[#1a1a1a]">
          <CardHeader>
            <CardTitle className="text-2xl text-white flex items-center gap-2">
              <User className="w-6 h-6 text-blue-400" />
              Your Learning Profile
            </CardTitle>
            <CardDescription className="text-[#8a8a8a]">
              Help us personalize your learning experience. The AI tutor will use this information to tailor explanations and examples to your context.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Why this matters</p>
                  <p className="text-[#8a8a8a] text-sm mt-1">
                    When you ask the AI tutor questions, it will use examples from your industry and adjust complexity based on your experience level.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-white flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" />
                  Industry
                </Label>
                <Select value={profile.industry} onValueChange={(v) => setProfile({...profile, industry: v})}>
                  <SelectTrigger className="bg-[#050505] border-[#1a1a1a] text-white">
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#1a1a1a]">
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind} className="text-white hover:bg-[#1a1a1a]">
                        {ind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white flex items-center gap-2 mb-2">
                  <Briefcase className="w-4 h-4" />
                  Role
                </Label>
                <Select value={profile.role} onValueChange={(v) => setProfile({...profile, role: v})}>
                  <SelectTrigger className="bg-[#050505] border-[#1a1a1a] text-white">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#1a1a1a]">
                    {roles.map((role) => (
                      <SelectItem key={role} value={role} className="text-white hover:bg-[#1a1a1a]">
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" />
                  Company Size
                </Label>
                <Select value={profile.company_size} onValueChange={(v) => setProfile({...profile, company_size: v})}>
                  <SelectTrigger className="bg-[#050505] border-[#1a1a1a] text-white">
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#1a1a1a]">
                    {companySizes.map((size) => (
                      <SelectItem key={size} value={size} className="text-white hover:bg-[#1a1a1a]">
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white flex items-center gap-2 mb-2">
                  <Brain className="w-4 h-4" />
                  AI Experience Level
                </Label>
                <Select value={profile.ai_experience} onValueChange={(v) => setProfile({...profile, ai_experience: v})}>
                  <SelectTrigger className="bg-[#050505] border-[#1a1a1a] text-white">
                    <SelectValue placeholder="Select your experience" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#1a1a1a]">
                    {aiExperiences.map((exp) => (
                      <SelectItem key={exp.value} value={exp.value} className="text-white hover:bg-[#1a1a1a]">
                        {exp.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={handleSave} className="w-full btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Saved!
                </>
              ) : (
                "Save Profile"
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
